import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Table, Button, Row, Col, Card, Spinner } from 'react-bootstrap';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import "./IncomeAndExpenses.css";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const IncomeAndExpenses = () => {
    const [data, setData] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData(year);
    }, [year]);

    const fetchData = async (selectedYear) => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.sominnovations.xyz/Branches/income-expenses-by-month/`, {
                params: { year: selectedYear },
            });
            setData(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePreviousYear = () => setYear((prev) => prev - 1);
    const handleNextYear = () => setYear((prev) => prev + 1);


    // Helper to safely parse currency values (string or number)
    const parseCurrency = (value) => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
        }
        return 0;
    };

    // Helper to display currency with Ksh
    const formatCurrency = (value) => {
        const val = parseCurrency(value);
        return `Ksh ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Prepare data for charts
    const monthlyData = data?.income_and_expenses_by_month;
    const months = monthlyData ? Object.keys(monthlyData) : [];
    const totalSales = monthlyData ? months.map((month) => parseCurrency(monthlyData[month].total_sales)) : [];
    const totalExpenses = monthlyData ? months.map((month) => parseCurrency(monthlyData[month].total_expenses)) : [];
    const netIncome = monthlyData ? months.map((month) => parseCurrency(monthlyData[month].net_income)) : [];

    const barChartData = {
        labels: months,
        datasets: [
            { label: 'Total Sales', data: totalSales, backgroundColor: '#4e73df' },
            { label: 'Total Expenses', data: totalExpenses, backgroundColor: '#e74a3b' },
            { label: 'Net Income', data: netIncome, backgroundColor: '#1cc88a' },
        ],
    };

    const lineChartData = {
        labels: months,
        datasets: [
            {
                label: 'Net Income Over Time',
                data: netIncome,
                fill: false,
                borderColor: '#1cc88a',
                tension: 0.1,
            },
        ],
    };
    return (
        <Container
            className="Container"
            fluid
            style={{

                marginTop: '20px',
                marginBottom: '20px',
            }}
        >
            <Card className="shadow-lg">
                <Row className="mb-4 text-center">
                    <Col xs={4}>
                        <Button variant="primary" onClick={handlePreviousYear} disabled={loading} className="w-100">
                            Previous Year
                        </Button>
                    </Col>
                    <Col xs={4} className="d-flex align-items-center justify-content-center">
                        <h4 className="text-primary fw-bold mb-0">{year}</h4>
                    </Col>
                    <Col xs={4}>
                        <Button variant="secondary" onClick={handleNextYear} disabled={loading} className="w-100">
                            Next Year
                        </Button>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading...</p>
                    </div>
                ) : (
                    data && (
                        <>
                            <Row className="mb-4">
                                <Col>
                                    <div style={{ width: '100%' }}>
                                        <Bar
                                            data={barChartData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: { legend: { position: 'top' } },
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col>
                                    <div style={{ width: '100%' }}>
                                        <Line
                                            data={lineChartData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: { legend: { position: 'top' } },
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Table striped bordered hover className=" mt-4" style={{ width: "100%" }}>
                                <thead className="table-primary">
                                    <tr>
                                        <th>Month</th>
                                        <th>Total Sales</th>
                                        <th>Total Expenses</th>
                                        <th>Net Income</th>
                                        <th>Total Profit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {months.map((month) => (
                                        <tr key={month}>
                                            <td>{month}</td>
                                            <td>{formatCurrency(monthlyData[month].total_sales)}</td>
                                            <td>{formatCurrency(monthlyData[month].total_expenses)}</td>
                                            <td
                                                className={
                                                    parseCurrency(monthlyData[month].net_income) > 0
                                                        ? 'text-success'
                                                        : 'text-danger'
                                                }
                                            >
                                                {formatCurrency(monthlyData[month].net_income)}
                                            </td>
                                            <td className='text-success'>{formatCurrency(monthlyData[month].total_profit)}</td>

                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <div className="mt-4 text-center">
                                <h5 className="text-success fw-bold">Yearly Totals</h5>
                                <p>
                                    <strong>Total Income:</strong> {formatCurrency(data.total_yearly_income)}
                                </p>
                                <p>
                                    <strong>Total Expenses:</strong> {formatCurrency(data.total_yearly_expenses)}
                                </p>
                                <p>
                                    <strong>Net Income:</strong> {formatCurrency(data.total_yearly_net_income)}
                                </p>
                            </div>
                        </>
                    )
                )}
            </Card>
        </Container>
    );
};

export default IncomeAndExpenses;
